import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    getModelOptionsForYear,
    getYearOptions,
    VehicleYmmGroup,
} from '../../../../models/vehicle-ymm';
import { Dropdown, PrimaryButton } from '../../../common';
import { buildPath } from '../../../../routes';
import { useHomeContent } from '../../../../views/home-page/hooks/use-home-content';

import './ymm-selector.scss';
import { useAnalytics } from '../../../../hooks/use-analytics';
import ServerContext from '../../../../contexts/serverContext';
import { VehicleListServiceAem } from '../../../../services/vehicle-list-service-aem/vehicle-list-service-aem';
import { getAemVehicleModelData } from '../../../../hooks/use-aem-vehicle-model-data';
import { useExperienceContent } from '../../../../hooks/use-server-content';
import { CxYmm } from '../../../../models/experiencefragments/cx-ymm';

const DEFAULT_VEHICLE_GROUP: VehicleYmmGroup[] = [];
const DEFAULT_YEAR = 0;
const DEFAULT_CX_YEAR = '2022';
const DEFAULT_MODEL = '';

interface Props {
    ymmHeaderLabel: string;
    yearDropdownLabel: string;
    modelDropdownLabel: string;
    submitLabel: string;
    vehicleDropdownData?: VehicleYmmGroup[];
    analyticsEventName?: string;
    articlesSearch?: boolean;
}

const YMMSelector = (props: Props) => {
    const [fireEvent] = useAnalytics();
    const { currentLanguageRegionCode, currentRegionCode, brand } = useContext(
        ServerContext
    );
    const [modelsByYear, setModelsByYear] = useState<VehicleYmmGroup[]>(
        props.vehicleDropdownData || DEFAULT_VEHICLE_GROUP
    );
    const [selectedYear, setSelectedYear] = useState<number>(DEFAULT_YEAR);
    const [selectedModel, setSelectedModel] = useState<string>(DEFAULT_MODEL);
    const [yearErrorMessage, setYearErrorMessage] = useState<
        string | undefined
    >('');
    const [modelErrorMessage, setModelErrorMessage] = useState<
        string | undefined
    >('');
    const yearOptions: string[] = getYearOptions(modelsByYear);
    const modelOptions: string[] = getModelOptionsForYear(
        modelsByYear,
        selectedYear
    );
    const history = useHistory();
    const homeContent = useHomeContent(
        props.articlesSearch ? 'learning-hub/landing-page' : 'landing'
    );

    const [yearErrorMessageText, modelErrorMessageText] = [
        homeContent?.vehicleSelectorYearErrorMsg,
        homeContent?.vehicleSelectorModelErrorMsg,
    ];

    const [cxYmmContent] = useExperienceContent<CxYmm>(
        'img-cx-ymm',
        'cx-ymm',
        'img_cx_ymm'
    );

    const selectVehicle = async (model: string) => {
        const vehicleYmm = await getAemVehicleModelData(
            brand,
            currentRegionCode,
            currentLanguageRegionCode,
            selectedYear,
            selectedModel,
            'DISPLAY_MODEL_NAME'
        );

        if (model && selectedYear) {
            if (props.articlesSearch) {
                const models = props.vehicleDropdownData?.find(
                    year => year.year == selectedYear
                );
                const seoKey = models?.vehicles.find(
                    vehicle => vehicle.marketName === model
                );
                seoKey &&
                    history.push(
                        buildPath(
                            'CxHandler',
                            seoKey.seoKey,
                            cxYmmContent?.isDropdownBypassInHomePage
                                ? DEFAULT_CX_YEAR
                                : String(selectedYear)
                        )
                    );
            } else if (vehicleYmm?.seoKey) {
                history.push(
                    buildPath(
                        'VehicleYmmView',
                        vehicleYmm.seoKey,
                        String(selectedYear)
                    )
                );
            }
        }
    };

    const onYearDropdownChange = (value: string) => {
        setYearErrorMessage('');
        setSelectedModel(DEFAULT_MODEL);
        setSelectedYear(parseInt(value, 10));
    };

    const onModelDropdownChange = (value: string) => {
        setModelErrorMessage('');
        setSelectedModel(value);
    };

    const onModelDropdownClick = () => {
        if (!selectedYear) {
            setYearErrorMessage(yearErrorMessageText);
        }
    };

    const onYMMSubmit = () => {
        setYearErrorMessage('');
        if (!selectedYear) {
            setYearErrorMessage(yearErrorMessageText);
        } else if (
            selectedYear &&
            (!selectedModel || selectedModel === props.modelDropdownLabel)
        ) {
            setModelErrorMessage(modelErrorMessageText);
        } else if (props.articlesSearch) {
            selectVehicle(selectedModel);
            props.analyticsEventName &&
                fireEvent(
                    props.analyticsEventName,
                    undefined,
                    {
                        cxVehicle: {
                            cxVinOrYmm:
                                selectedYear.toString() + ':' + selectedModel,
                        },
                    },
                    false
                );
        } else {
            props.analyticsEventName &&
                fireEvent(
                    props.analyticsEventName,
                    undefined,
                    {
                        smashHomeClick: {
                            smashHomeSearchContentCta: 'ymm',
                        },
                    },
                    false
                );
            selectVehicle(selectedModel);
        }
    };

    useEffect(() => {
        if (!props.vehicleDropdownData) {
            new VehicleListServiceAem()
                .getVehicles(
                    brand,
                    currentRegionCode,
                    currentLanguageRegionCode
                )
                .then(setModelsByYear)
                .catch(console.error);

            return function cleanup() {
                setModelsByYear(DEFAULT_VEHICLE_GROUP);
            };
        }
    }, []);

    return (
        <div className="smash-ymm-container">
            <div className="selector-label">{props.ymmHeaderLabel}</div>
            <div className="ymm-container-dropdown">
                <Dropdown
                    label={props.yearDropdownLabel}
                    className="year-dropdown"
                    options={yearOptions}
                    value={selectedYear}
                    errorMessage={yearErrorMessage}
                    onChange={onYearDropdownChange}
                />
                <Dropdown
                    label={props.modelDropdownLabel}
                    disabled={!selectedYear}
                    options={modelOptions}
                    className="model-dropdown"
                    value={selectedModel}
                    errorMessage={modelErrorMessage}
                    onChange={onModelDropdownChange}
                    onClick={onModelDropdownClick}
                />
            </div>
            <PrimaryButton
                className="smash-vehicle-selector-button-ymm"
                color="dark"
                fill="fill"
                chevron={true}
                onClick={onYMMSubmit}
            >
                {props.submitLabel}
            </PrimaryButton>
        </div>
    );
};

export default YMMSelector;
